import React from 'react';
import { BrowserRouter as Router, Route, Routes, Outlet, Navigate } from 'react-router-dom';
import MemberManagement from './pages/MemberManagement';
import Layout from './pages/Layout';
import { createTheme, ThemeProvider } from '@mui/material';
import ContractStepper from './pages/CreateMember/ContractStepper';
import NotFound from './pages/NotFound';
import { LoadingProvider, useLoading } from './contexts/LoadingContext';
import { HeaderProvider } from './contexts/HeaderContext';
import BookingCalendar from './pages/Calendar';
import { MembersProvider } from './contexts/MemberContext';
import { BookingsProvider } from './contexts/BookingContext';
import { SettingProvider } from './contexts/SettingContext';
import ServiceTable from './pages/Settings/ServiceTable';
import PaymentHistory from './pages/Settings/PaymentHistory';
import SignIn from './pages/Auth/SignIn';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ProfilePage } from './pages/Settings/ProfilePage';
import { DialogProvider } from './contexts/DialogContext';
import Verified from './pages/Auth/Verified';
import Account from './pages/Settings/Account';
import { RedirectUpdateUserInfo } from './components/Redirect';
import TermsAndConditions from './terms';
import Landing from './pages/Landing';
import About from './pages/Landing/About';
import Services from './pages/Landing/Services';
import Contact from './pages/Landing/Contact';
import ResetSuccess from './pages/Auth/ResetSuccess';
const theme = createTheme({
  palette: {
    primary: {
      main: "#407CBD", // Primary color
      light: "#99BAD4", // Lighter shade
      dark: "#3D587D", // Darker shade
      contrastText: "#ffffff", // Contrast text
    },
    secondary: {
      main: "#F6EAD1", // Secondary color
      light: "#FFDB89", // Lighter shade
      dark: "#E2EFEC", // Darker shade
      contrastText: "#3D587D", // Contrast text
    },
    error: {
      main: "#b00020",
      light: "#cf3337",
      dark: "#8b0016",
      contrastText: "#ffffff",
    },
    warning: {
      main: "#f57f17",
      light: "#ffb04c",
      dark: "#bc5100",
      contrastText: "#000000",
    },
    info: {
      main: "#1976d2",
      light: "#63a4ff",
      dark: "#004ba0",
      contrastText: "#ffffff",
    },
    success: {
      main: "#388e3c",
      light: "#6abf69",
      dark: "#00600f",
      contrastText: "#ffffff",
    },
  },
  components: {
    MuiListItem: {
      styleOverrides: {
        root: {
          color: "#407CBD", // Set menu text color to primary color
          transition: 'transform 0.2s, box-shadow 0.2s',
          '&:hover': {
            transform: 'scale(1.05)',
            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: '#99BAD4', // Lighter primary color on hover
          },
        },
        containedPrimary: {
          '&:hover': {
            backgroundColor: '#99BAD4',
          },
        },
        containedSecondary: {
          '&:hover': {
            backgroundColor: '#FFDB89', // Secondary lighter color for hover
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          '&:hover': {
            color: '#99BAD4', // Lighter primary color on hover for icons
          },
        },
      },
    },
  },
  typography: {
    fontSize: 16, // Sets the base font size to 16pt
  },
});



const App: React.FC = () => (
  <ThemeProvider theme={theme}>
    <AuthProvider>
      <LoadingProvider>
        <MembersProvider>
          <BookingsProvider>
            <SettingProvider>
              <DialogProvider>
                  <MainComponent />
                </DialogProvider>
              </SettingProvider>
            </BookingsProvider>
        </MembersProvider>
      </LoadingProvider>
    </AuthProvider>
  </ThemeProvider>
);

const ProtectedRoute: React.FC = () => {
  const { loggedIn } = useAuth();

  if (loggedIn){
    return <Outlet />;
  } else{
    return <Navigate to="/signin" />;
  }
};


const MainComponent: React.FC = () => {
  const { isLoading } = useLoading();
  const { isInitialized } = useAuth();

  // Show a loading state while auth is being initialized
  if (!isInitialized) {
    return <div style={{ cursor: isLoading ? 'wait' : 'default' }}>Loading...</div>;
  }

  return (
    <div style={{ cursor: isLoading ? 'wait' : 'default' }}>
      <Router>
        <Routes>
          {/* Landing page routes */}
          <Route path="/" element={<Landing />} />
          <Route path="/about" element={<About />} />
          <Route path="/services" element={<Services />} />
          <Route path="/contact" element={<Contact />} />

          {/* Protected dashboard routes */}
          <Route element={<ProtectedRoute />}>
            <Route path="/dashboard" element={<HeaderProvider><Layout /></HeaderProvider>}>
              <Route index element={<BookingCalendar />} />
              <Route path="member" element={<MemberManagement />} />
              <Route path="setting" >
                <Route index element={<NotFound />} />
                <Route path="profile" element={<ProfilePage />} />
                <Route path="account" element={<Account />} />
                <Route path="service" element={<ServiceTable />} />
                <Route path="payments" element={<PaymentHistory />} />
              </Route>
            </Route>
          </Route>

          {/* Auth and other routes */}
          <Route path="/member/create" element={<ContractStepper />} />
          <Route path="/signin" element={<SignIn />} />
          <Route path="/verified" element={<Verified />} />
          <Route path="/resetSuccess" element={<ResetSuccess />} />
          <Route path="/updateUserInfo" element={<RedirectUpdateUserInfo />} />
          <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
        </Routes>
      </Router>
    </div>
  );
};


export default App;
